import { CSSProperties } from 'react';
import { IconComponent } from '@backstage/frontend-plugin-api';
import { gcpIcons, GcpIconName } from '@internal/plugin-gcp-network-diagram';
import {
  type SimpleIcon,
  siAngular,
  siAnthropic,
  siApacheairflow,
  siApachekafka,
  siArgo,
  siAtlassian,
  siBackstage,
  siClaude,
  siCloudflare,
  siCodeigniter,
  siDocker,
  siElasticsearch,
  siFirebase,
  siGithub,
  siGithubactions,
  siGo,
  siGoogleanalytics,
  siGooglebigquery,
  siGooglecloud,
  siGooglepubsub,
  siGrafana,
  siHelm,
  siHubspot,
  siJavascript,
  siJira,
  siKotlin,
  siKubernetes,
  siLaravel,
  siMailchimp,
  siMetabase,
  siMongodb,
  siMysql,
  siN8n,
  siNextdotjs,
  siNginx,
  siNodedotjs,
  siOkta,
  siOpentelemetry,
  siPagerduty,
  siPaypal,
  siPhp,
  siPostgresql,
  siPrometheus,
  siPython,
  siRabbitmq,
  siReact,
  siRedis,
  siRust,
  siSentry,
  siShopify,
  siStripe,
  siSvelte,
  siSymfony,
  siTailwindcss,
  siTerraform,
  siTypescript,
  siVite,
  siVuedotjs,
  siWoocommerce,
  siWordpress,
} from 'simple-icons';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import ApiIcon from '@mui/icons-material/Api';
import ChatIcon from '@mui/icons-material/Chat';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GroupsIcon from '@mui/icons-material/Groups';
import ImageIcon from '@mui/icons-material/Image';
import InsightsIcon from '@mui/icons-material/Insights';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TerminalIcon from '@mui/icons-material/Terminal';
import TranslateIcon from '@mui/icons-material/Translate';
import WebhookIcon from '@mui/icons-material/Webhook';

/**
 * The catalog annotation apps set to pick their own icon, e.g.
 *
 *     metadata:
 *       annotations:
 *         backstage.ds25.io/icon: symfony
 *
 * The value is a name from {@link catalogIcons}; unknown names fall back to the
 * default icon for the entity's kind.
 */
export const ICON_ANNOTATION = 'backstage.ds25.io/icon';

// The sizes MUI's SvgIcon uses, so these line up with Backstage's own icons
const sizes: Record<string, CSSProperties['width']> = {
  inherit: '1em',
  small: '1.25rem',
  medium: '1.5rem',
  large: '2.1875rem',
};

const iconStyle = (fontSize = 'medium'): CSSProperties => ({
  width: sizes[fontSize],
  height: sizes[fontSize],
  verticalAlign: 'middle',
  flexShrink: 0,
});

/**
 * Brand colours that are near-black (Next.js, Symfony, GitHub) vanish on the
 * dark theme and near-white/yellow ones (JavaScript, Mailchimp) on the light
 * one; those take the surrounding text colour instead.
 */
const brandFill = (hex: string) => {
  const [r, g, b] = [0, 2, 4].map(i => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.05 || luminance > 0.6 ? 'currentColor' : `#${hex}`;
};

const brand = (icon: SimpleIcon): IconComponent => {
  const Icon: IconComponent = ({ fontSize }) => (
    <svg
      viewBox="0 0 24 24"
      fill={brandFill(icon.hex)}
      aria-hidden
      style={iconStyle(fontSize)}
    >
      <path d={icon.path} />
    </svg>
  );
  Icon.displayName = `BrandIcon(${icon.slug})`;
  return Icon;
};

// The GCP SVGs share internal class names (.cls-1 …) and would collide if
// inlined, so they are rendered as images, as in the network diagram
const gcp = (name: GcpIconName): IconComponent => {
  const Icon: IconComponent = ({ fontSize }) => (
    <img src={gcpIcons[name]} alt="" aria-hidden style={iconStyle(fontSize)} />
  );
  Icon.displayName = `GcpIcon(${name})`;
  return Icon;
};

/** GCP product icons, by name; also used for ingested GCP resources. */
export const gcpIconComponents = Object.fromEntries(
  (Object.keys(gcpIcons) as GcpIconName[]).map(name => [name, gcp(name)]),
) as Record<GcpIconName, IconComponent>;

/**
 * Every icon an app may pick with {@link ICON_ANNOTATION}. Names are
 * kebab-case and part of our catalog contract: add freely, but don't rename
 * or remove one that entities might use. Components are created once, here:
 * a new component type per render would remount every entity link.
 */
export const catalogIcons: Record<string, IconComponent> = {
  // Languages and frameworks
  angular: brand(siAngular),
  codeigniter: brand(siCodeigniter),
  go: brand(siGo),
  javascript: brand(siJavascript),
  kotlin: brand(siKotlin),
  laravel: brand(siLaravel),
  nextjs: brand(siNextdotjs),
  nodejs: brand(siNodedotjs),
  php: brand(siPhp),
  python: brand(siPython),
  react: brand(siReact),
  rust: brand(siRust),
  svelte: brand(siSvelte),
  symfony: brand(siSymfony),
  tailwind: brand(siTailwindcss),
  typescript: brand(siTypescript),
  vite: brand(siVite),
  vue: brand(siVuedotjs),
  wordpress: brand(siWordpress),

  // Data, messaging and infrastructure
  airflow: brand(siApacheairflow),
  argo: brand(siArgo),
  bigquery: brand(siGooglebigquery),
  cloudflare: brand(siCloudflare),
  docker: brand(siDocker),
  elasticsearch: brand(siElasticsearch),
  firebase: brand(siFirebase),
  'google-cloud': brand(siGooglecloud),
  helm: brand(siHelm),
  kafka: brand(siApachekafka),
  kubernetes: brand(siKubernetes),
  mongodb: brand(siMongodb),
  mysql: brand(siMysql),
  nginx: brand(siNginx),
  postgresql: brand(siPostgresql),
  pubsub: brand(siGooglepubsub),
  rabbitmq: brand(siRabbitmq),
  redis: brand(siRedis),
  terraform: brand(siTerraform),

  // Observability and tooling
  backstage: brand(siBackstage),
  github: brand(siGithub),
  'github-actions': brand(siGithubactions),
  grafana: brand(siGrafana),
  metabase: brand(siMetabase),
  opentelemetry: brand(siOpentelemetry),
  pagerduty: brand(siPagerduty),
  prometheus: brand(siPrometheus),
  sentry: brand(siSentry),

  // SaaS and business
  anthropic: brand(siAnthropic),
  atlassian: brand(siAtlassian),
  claude: brand(siClaude),
  'google-analytics': brand(siGoogleanalytics),
  hubspot: brand(siHubspot),
  jira: brand(siJira),
  mailchimp: brand(siMailchimp),
  n8n: brand(siN8n),
  okta: brand(siOkta),
  paypal: brand(siPaypal),
  shopify: brand(siShopify),
  stripe: brand(siStripe),
  woocommerce: brand(siWoocommerce),

  // Google Cloud products
  'gcp-cloud-dns': gcpIconComponents.cloudDns,
  'gcp-cloud-nat': gcpIconComponents.cloudNat,
  'gcp-cloud-router': gcpIconComponents.cloudRouter,
  'gcp-cloud-sql': gcpIconComponents.cloudSql,
  'gcp-cloud-storage': gcpIconComponents.cloudStorage,
  'gcp-firewall': gcpIconComponents.cloudFirewallRules,
  'gcp-gke': gcpIconComponents.googleKubernetesEngine,
  'gcp-load-balancing': gcpIconComponents.cloudLoadBalancing,
  'gcp-memorystore': gcpIconComponents.memorystore,
  'gcp-network': gcpIconComponents.cloudNetwork,
  'gcp-vpc': gcpIconComponents.virtualPrivateCloud,

  // Generic, for anything without a logo
  api: ApiIcon,
  chat: ChatIcon,
  cloud: CloudIcon,
  code: CodeIcon,
  cron: ScheduleIcon,
  dashboard: DashboardIcon,
  database: StorageIcon,
  docs: DescriptionIcon,
  email: EmailIcon,
  analytics: InsightsIcon,
  image: ImageIcon,
  map: MapIcon,
  mobile: PhoneIphoneIcon,
  notifications: NotificationsIcon,
  payments: PaymentsIcon,
  proxy: AltRouteIcon,
  queue: QueueIcon,
  robot: SmartToyIcon,
  search: SearchIcon,
  security: SecurityIcon,
  shop: StorefrontIcon,
  team: GroupsIcon,
  terminal: TerminalIcon,
  translate: TranslateIcon,
  user: PersonIcon,
  webhook: WebhookIcon,
  website: LanguageIcon,
  worker: EngineeringIcon,
};

/** The icon an entity picked for itself, if it names one we know. */
export const annotatedIcon = (annotations: Record<string, string> = {}) => {
  const name = annotations[ICON_ANNOTATION]?.trim().toLowerCase();
  // hasOwn: a value like "constructor" must not resolve to Object's own props
  return name && Object.hasOwn(catalogIcons, name)
    ? catalogIcons[name]
    : undefined;
};
